import React, { useState, useEffect } from 'react';
import { pharmacyWardAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const today = () => new Date().toISOString().slice(0,10);
const fmt = d => d ? new Date(d).toLocaleString('en-PK',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const SOURCE_BADGE = {
    'Ward': { bg:'#dbeafe', color:'#1d4ed8' },
    'OPD':  { bg:'#d1fae5', color:'#065f46' },
};
const TYPE_BADGE = {
    'Issued':   { bg:'#dcfce7', color:'#15803d' },
    'Returned': { bg:'#fee2e2', color:'#b91c1c' },
};

export default function PharmacyWardReport() {
    const [from,   setFrom]   = useState(today());
    const [to,     setTo]     = useState(today());
    const [source, setSource] = useState('');      // '' = All, 'ward', 'opd'
    const [type,   setType]   = useState('');      // '' = All, 'issued', 'returned'
    const [rows,   setRows]   = useState([]);
    const [loading,setLoading]= useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await pharmacyWardAPI.getUnifiedReport({
                from, to,
                source: source || undefined,
                type:   type   || undefined,
            });
            setRows(r.data || []);
        } catch { toast.error('Failed to load report'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const totalQty = rows.reduce((s,r) => s + (parseInt(r.qty) || 0), 0);

    const print = () => {
        const escape = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        const trs = rows.map(r =>
            `<tr>
                <td>${escape(r.mrn)||'—'}</td>
                <td>${escape(r.patient_name)||'—'}</td>
                <td>${escape(r.medicine_name)}</td>
                <td style="text-align:center">${r.qty||0}</td>
                <td>${escape(r.source)}</td>
                <td>${escape(r.ward_name)||'—'}</td>
                <td>${escape(r.type)}</td>
                <td>${escape(r.done_by)||'—'}</td>
                <td>${fmt(r.date)}</td>
            </tr>`
        ).join('');
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Pharmacy Medicine Report</title>
<style>body{font-family:Arial,sans-serif;font-size:11px;padding:20px}
h2{text-align:center}table{width:100%;border-collapse:collapse}
th,td{border:1px solid #ccc;padding:5px 7px;text-align:left}
th{background:#f0f0f0;font-weight:bold}
.issued{color:#15803d;font-weight:600}.returned{color:#b91c1c;font-weight:600}
.ward{color:#1d4ed8}.opd{color:#065f46}</style>
</head><body>
<h2>Pharmacy — Medicine Report</h2>
<p>Period: ${escape(from)} to ${escape(to)} &nbsp;|&nbsp; Total Records: ${rows.length} &nbsp;|&nbsp; Total Qty: ${totalQty}</p>
<table><thead><tr>
<th>MRN</th><th>Patient</th><th>Medicine</th><th>Qty</th><th>Source</th><th>Ward/OPD</th><th>Type</th><th>By</th><th>Date</th>
</tr></thead><tbody>${trs}</tbody></table>
</body></html>`;
        const win = window.open('', '_blank', 'width=1000,height=700');
        if (win) { win.document.write(html); win.document.close(); win.print(); }
    };

    const card = { background:'white', borderRadius:8, border:'1px solid #e2e8f0', padding:16, marginBottom:16 };
    const inp  = { padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, width:'100%', boxSizing:'border-box' };
    const btn  = (bg) => ({ padding:'8px 20px', background:bg, color:'white', border:'none', borderRadius:6, fontWeight:700, fontSize:13, cursor:'pointer' });

    return (
        <div style={{ maxWidth:1200 }}>
            <h2 style={{ fontSize:18, fontWeight:800, color:'#1e3a5f', marginBottom:20 }}>Pharmacy — Medicine Report</h2>

            {/* Filters */}
            <div style={{ ...card, display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr auto auto', gap:12, alignItems:'end' }}>
                <div>
                    <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>From</label>
                    <input type="date" style={inp} value={from} onChange={e=>setFrom(e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>To</label>
                    <input type="date" style={inp} value={to} onChange={e=>setTo(e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Source</label>
                    <select style={inp} value={source} onChange={e=>setSource(e.target.value)}>
                        <option value="">All (Ward + OPD)</option>
                        <option value="ward">Ward Only</option>
                        <option value="opd">OPD Only</option>
                    </select>
                </div>
                <div>
                    <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Type</label>
                    <select style={inp} value={type} onChange={e=>setType(e.target.value)}>
                        <option value="">Issued + Returned</option>
                        <option value="issued">Issued Only</option>
                        <option value="returned">Returned Only</option>
                    </select>
                </div>
                <button style={btn('#1e3a5f')} onClick={load}>Search</button>
                <button style={btn('#059669')} onClick={print}>Print</button>
            </div>

            {/* Summary */}
            <div style={{ ...card, display:'flex', gap:32 }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:28, fontWeight:800, color:'#1e3a5f' }}>{rows.length}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>Total Records</div>
                </div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:28, fontWeight:800, color:'#059669' }}>{totalQty}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>Total Qty</div>
                </div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:28, fontWeight:800, color:'#1d4ed8' }}>
                        {rows.filter(r=>r.source==='Ward').length}
                    </div>
                    <div style={{ fontSize:12, color:'#64748b' }}>Ward</div>
                </div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:28, fontWeight:800, color:'#065f46' }}>
                        {rows.filter(r=>r.source==='OPD').length}
                    </div>
                    <div style={{ fontSize:12, color:'#64748b' }}>OPD</div>
                </div>
            </div>

            {/* Table */}
            <div style={{ ...card, overflowX:'auto' }}>
                {loading ? (
                    <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>Loading...</div>
                ) : rows.length === 0 ? (
                    <div style={{ textAlign:'center', color:'#94a3b8', padding:32 }}>No records found</div>
                ) : (
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                        <thead>
                            <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
                                {['MRN','Patient','Medicine','Qty','Source','Ward / OPD','Type','By','Date'].map(h=>(
                                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontWeight:700, color:'#475569', whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r,i)=>{
                                const sb = SOURCE_BADGE[r.source] || {};
                                const tb = TYPE_BADGE[r.type]     || {};
                                return (
                                    <tr key={i} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'white':'#fafafa' }}>
                                        <td style={{ padding:'7px 10px', fontWeight:600, color:'#6366f1' }}>{r.mrn||'—'}</td>
                                        <td style={{ padding:'7px 10px' }}>{r.patient_name||'—'}</td>
                                        <td style={{ padding:'7px 10px', fontWeight:600 }}>{r.medicine_name}</td>
                                        <td style={{ padding:'7px 10px', textAlign:'center', fontWeight:700 }}>{r.qty||0}</td>
                                        <td style={{ padding:'7px 10px' }}>
                                            <span style={{ background:sb.bg, color:sb.color, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>{r.source}</span>
                                        </td>
                                        <td style={{ padding:'7px 10px', color:'#374151' }}>{r.ward_name||'—'}</td>
                                        <td style={{ padding:'7px 10px' }}>
                                            <span style={{ background:tb.bg, color:tb.color, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>{r.type}</span>
                                        </td>
                                        <td style={{ padding:'7px 10px', color:'#64748b' }}>{r.done_by||'—'}</td>
                                        <td style={{ padding:'7px 10px', color:'#64748b', whiteSpace:'nowrap' }}>{fmt(r.date)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
